/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Pavel Režňák (reznak@cadwork.cz)
 */

import WebGLRenderGraph from "App/render/webgl/render_graph";
import Layer from "App/render/webgl/layers/layer";
import {Containers, Factory} from "App/utils/factory";
import {vec2} from "gl-matrix";
import WebGLContext from "App/web_gl_context";
import {logInfo} from "App/debug";
import {Framebuffer} from "App/utils/frame_buffer";
import View from "App/view/view";
import ShadowMap from "App/render/webgl/passes/shadow_map/shadow_map";
import ShadowMapPoisson from "App/render/webgl/passes/shadow_map/shadow_map_poisson";
import ShadowMapVariance from "App/render/webgl/passes/shadow_map_variance/shadow_map_variance";
import ShadowMapBlur from "App/render/webgl/passes/shadow_map_variance/shadow_map_blur";

export default
class ShadowMapLayer extends Layer {
    private _valid: boolean;
    private _lightValid: boolean;
    private _texture: WebGLTexture;
    private _poisson: WebGLTexture;
    private _poissonSize: vec2;
    private _size: number;
    private _pcMaxSize: number;
    private _mobileMaxSize: number;

    constructor(aRenderGraph: WebGLRenderGraph) {
        super(aRenderGraph);

        this._passesID = Containers.PASSES_SM;
        this._name = 'ShadowMaps';

        this._valid = false;       // True if Shadow Map is valid
        this._lightValid = false;  // True if light has not changed

        /** @type WebGLTexture */
        this._texture = null;      // Texture where Shadow Map depth is rendered
        /** @type WebGLTexture */
        this._poisson = null;      // Texture where lighting with poisson disk is calculated

        this._poissonSize = vec2.create();
        this._size = 0;            // Real size of the texture
        this._pcMaxSize = 2048;    // Max size for PC (maybe 4096?)
        this._mobileMaxSize = 1024; // Max size for mobile devices
    }

    /**
     * @return {WebGLTexture | null}
     */
    public getTexture(): WebGLTexture {
        if (this._valid) {
            return this._texture;
        }

        return null;
    }

    /**
     * @return {number}
     */
    public getTextureSize(): number {
        return this._size;
    }

    /**
     * @return {WebGLTexture | null}
     */
    public getPoissonTexture(): WebGLTexture {
        if (this._valid) {
            return this._poisson;
        }
        return null;
    }

    /**
     * @return {number}
     */
    public getPoissonTextureSize(): vec2 {
        return this._poissonSize;
    }

    /**
     * @override
     */
    public release() {
        this.invalidate();
        this._valid = false;
    }

    /**
     * @override
     */
    public invalidateFramebuffers() {
        if (!this._fboReady) {
            return;
        }

        super.invalidateFramebuffers();
        this.invalidateTexture();
    }

    /**
     * @override
     */
    public invalidateShaders() {
        if (!this._shadersReady) {
            return;
        }

        super.invalidateShaders();
        this._renderGraph.initializeShaders();
    }

    /**
     * @override
     */
    public initialize() {
        if (this._fboReady && this._shadersReady && this._passesReady) {
            return;
        }

        this._shadersID = Containers['SHADERS_SM_' + this._sceneAPI.shadowsModule.getShadowMapType().toUpperCase()];
        super.initialize();

        // Initialize shaders which are using lighting
        this.invalidateTexture();
        this._renderGraph.initializeShaders();
    };

    public invalidateTexture() {
        this._valid = false;
    };

    public invalidateLight() {
        this._lightValid = false;
        this.invalidateTexture();
    };

    /**
     * @override
     */
    protected _createFramebuffers() {
        let contentSize = this._context.getCanvas().getContentSize();
        let maxFloat = WebGLContext.caps.FLOAT32;
        let float32 = true;
        this._size = this._pcMaxSize;
        let type = this._sceneAPI.shadowsModule.getShadowMapType();

        // Limit texture size and float type on mobile devices
        if (!maxFloat || this._sceneAPI.isMobile()) {
            maxFloat = WebGLContext.caps.FLOAT16;
            this._size = this._mobileMaxSize;
            float32 = false;
        }

        // Show method
        logInfo("Shadow map [" + type.toUpperCase() + "]: " + (float32 ? "32" : "16") + " bit and " + this._size + "x" + this._size);

        // Helper variables
        let gl = this._context.getWebGLRenderingContext();
        let size = this.getTextureSize();

        // Shadow map with depth (for variance and pcf)
        this._framebuffers.add('shadowMapDepth',
            new Framebuffer(this._renderGraph, {
                scale: 1.0,
                size: [size, size],
                colorTextureFiltering: gl.LINEAR,
                colorTexturePixelDataType: maxFloat,
                createColorTexture: true,
                createDepthTexture: false,
                createDepthRenderBuffer: true
            })
        );

        // PCF
        if (type === "pcf") {
            let scale = 1.0;
            vec2.set(this._poissonSize, Math.floor(contentSize[0] * scale), Math.floor(contentSize[1] * scale));
            this._framebuffers.add('shadowMapPoisson',
                new Framebuffer(this._renderGraph, {
                    scale: scale,
                    size: this._poissonSize,
                    colorTextureFiltering: gl.NEAREST,
                    colorTexturePixelDataType: maxFloat,
                    createColorTexture: true,
                    createDepthTexture: false,
                    createDepthRenderBuffer: true
                })
            );
        } else if (type === "vsm") {
            // Variance shadow map, temporary
            this._framebuffers.add('shadowMapVarianceTmp',
                new Framebuffer(this._renderGraph, {
                    scale: 1.0,
                    size: [size, size],
                    colorTextureFiltering: gl.LINEAR,
                    colorTexturePixelDataType: maxFloat,
                    createColorTexture: true,
                    createDepthTexture: false,
                    createDepthRenderBuffer: false
                })
            );
        }
    }

    /**
     * @param {View} aView
     * @return {boolean}
     * @override
     */
    public render(aView: View): boolean {
        if (!this._sceneAPI.shadowsModule.getShadowMapEnabled()) {
            // this.invalidateTexture();
            return true;
        }

        if (!this.readyToRender()) {
            return true;
        }

        // Render
        let type = this._sceneAPI.shadowsModule.getShadowMapType();
        if (type === 'pcf') {
            return this._renderPCF(aView);
        } else if (type === 'vsm') {
            return this._renderVSM(aView);
        }
    }

    /**
     * @param {View} aView
     * @return {boolean}
     * @private
     */
    private _renderPCF(aView: View): boolean {
        let debug = this._sceneAPI.shadowsModule.getShadowMapDebugBuffer();
        let depthBuffer = this._framebuffers.get("shadowMapDepth");
        let poissonBuffer = this._framebuffers.get("shadowMapPoisson");

        switch (debug) {
            case 1:
                depthBuffer = this._renderGraph.getFramebuffersContainer().get("screen");
                break;
            case 2:
                poissonBuffer = this._renderGraph.getFramebuffersContainer().get("screen");
                break;
        }

        // Create depth map
        this._passes.get(ShadowMap.TypeName).render(
            aView,
            depthBuffer
        );

        if (debug === 1) {
            return false;
        }

        this._texture = depthBuffer.renderable.getColorTexture();

        // Create Poisson texture
        this._passes.get(ShadowMapPoisson.TypeName).render(
            aView,
            poissonBuffer,
            this._texture
        );

        if (debug === 2) {
            return false;
        }

        this._poisson = poissonBuffer.renderable.getColorTexture();
        this._valid = true;
        return true;
    }

    /**
     * @param {View} aView
     * @return {boolean}
     * @private
     */
    private _renderVSM(aView: View): boolean {
        // TODO: Here should be optimization waiting for the shadow map invalidation
        // Now it calculates shadow map at every frame
        /*if (this._valid && this._lightValid) {
            return true;
        }*/

        // Test if light should be recalculated
        if (!this._lightValid) {
            aView.getViewControls().getCamera().invalidateLight();
            this._lightValid = true;
        }

        let debug = this._sceneAPI.shadowsModule.getShadowMapDebugBuffer();
        let depthBuffer = (debug === 1) ? this._renderGraph.getFramebuffersContainer().get("screen") : this._framebuffers.get("shadowMapDepth");
        let tmpBuffer = (debug === 2) ? this._renderGraph.getFramebuffersContainer().get("screen") : this._framebuffers.get("shadowMapVarianceTmp");

        /* disable cull face for old or stupid models */
        let gl = this._context.getWebGLRenderingContext();
        let cullFaceBackup = gl.isEnabled(gl.CULL_FACE);
        gl.disable(gl.CULL_FACE);

        // Create depth map
        this._passes.get(ShadowMapVariance.TypeName).render(
            aView,
            depthBuffer
        );

        if (debug === 1) {
            return false;
        }

        // Blur it 1st pass
        this._passes.get(ShadowMapBlur.TypeName).render(
            aView,
            tmpBuffer,
            depthBuffer.renderable.getColorTexture(),
            vec2.fromValues(1, 0)
        );

        if (debug === 2) {
            return false;
        }

        // Blur it 2nd pass
        this._passes.get(ShadowMapBlur.TypeName).render(
            aView,
            depthBuffer,
            tmpBuffer.renderable.getColorTexture(),
            vec2.fromValues(0, 1)
        );

        // Restore culling
        if (cullFaceBackup) {
            gl.enable(gl.CULL_FACE);
        }

        this._texture = depthBuffer.renderable.getColorTexture();
        this._valid = true;
        return true;
    }
}
